import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { getAuthHeaders } from "../utils/getAuthHeaders";
import { getErrorMessage } from "../utils/getErrorMessage";
import { endpoints, urlResearchDagTemplate } from "../config/api";
import WorkflowPlanner from "./WorkflowPlanner";
import {
  P, PL, BORDER, ACCENT, INK, MUTED, LINE, SYNTH_ACCENT,
  CARD_SHADOW, CANVAS_BG, inp, lbl, outBtn, chip, truncate,
} from "./workflowTheme";
import {
  HOVER_CARD, PORT_HALO, inflate, placeHoverCard, hoverCardHeight, edgeObstacles,
} from "../utils/workflowCanvasLayout";
import {
  CONDITION_KIND, CONDITION_KIND_HINT, makeSelectorId, selectorGroups, selectorsFrom,
  applyConditional, applySelectorExpression, applySelectorId,
} from "../utils/workflowSelectors";
import { placeNodeHints } from "../utils/workflowNodePlacement";
import {
  SYNTH_UID, NODE_W, NODE_H, TERMINAL, computeLayout,
} from "../utils/workflowLayout";
import {
  routeEdge, obstaclesFor, sampleCubic, sampleLane, edgeEndpoints,
} from "../utils/workflowEdgeRouting";
import {
  initialTrail, pushPage, prevCursor, canGoPrev, canGoNext, readNextCursor,
} from "../utils/cursorPageTrail";

// ─── helpers ──────────────────────────────────────────────────────────────────

const slugify = (s) =>
  String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "node";

const makeNodeId = (name, used) => {
  const base = slugify(name);
  if (!used.has(base)) return base;
  let n = 2;
  while (used.has(`${base}_${n}`)) n++;
  return `${base}_${n}`;
};

const uid = () => `${Date.now()}${Math.random().toString(36).slice(2, 7)}`;

const ZOOM_MIN = 0.5, ZOOM_MAX = 1.5;
const clampZoom = z => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));

/** Does a directed path exist from `a` to `b`? Used to keep the graph acyclic. */
const reaches = (a, b, edges) => {
  const seen = new Set([a]);
  const stack = [a];
  while (stack.length) {
    const cur = stack.pop();
    if (cur === b) return true;
    for (const e of edges) {
      if (e.from === cur && !seen.has(e.to)) { seen.add(e.to); stack.push(e.to); }
    }
  }
  return false;
};

/**
 * Plain vertical cubic: leaves the source's bottom edge, enters the target's
 * top edge. No lateral offset, so edges never detour sideways or zig-zag.
 */
const edgePath = (x1, y1, x2, y2) => {
  const c = Math.max(28, Math.abs(y2 - y1) * 0.4);
  return `M ${x1} ${y1} C ${x1} ${y1 + c}, ${x2} ${y2 - c}, ${x2} ${y2}`;
};

// ─── design tokens ────────────────────────────────────────────────────────────
// Shared with the AI planner — see ./workflowTheme.

// edges: light grey at rest, a shade darker when their node is selected/hovered
const EDGE_IDLE = "#cbd5e1";
const EDGE_HOT = "#64748b";
const EDGE_DIM_OPACITY = 0.35;

// ─── canvas primitives ────────────────────────────────────────────────────────

const Terminal = ({ x, y, label, tone }) => (
  <div style={{
    position: "absolute", left: x, top: y, width: TERMINAL, height: TERMINAL,
    borderRadius: "50%", background: "#fff",
    border: `1.5px solid ${tone === "start" ? "#c7d2fe" : "#e2e8f0"}`,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "8px", fontWeight: 800, letterSpacing: "0.06em",
    color: tone === "start" ? ACCENT : MUTED,
    boxShadow: "0 1px 2px rgba(15,23,42,0.06)", zIndex: 3,
  }}>{label}</div>
);

/** Four ports; each opens the add-node / add-edge menu for that direction. */
const AgentNode = ({
  node, pos, areaName, accent, selected, hovered, connectState, issueCount, menuDir,
  onSelect, onHover, onPort, onRemove,
}) => {
  const invalid = connectState === "invalid";
  const active = selected || hovered || !!menuDir;

  const handle = (dir, style) => (
    <button
      key={dir}
      title="Add a node or an edge here"
      onClick={e => { e.stopPropagation(); onPort(dir); }}
      style={{
        position: "absolute", width: "18px", height: "18px", borderRadius: "50%",
        background: "#fff", border: `2px solid ${active ? ACCENT : "#a5b4fc"}`,
        color: ACCENT, fontSize: "12px", fontWeight: 800, lineHeight: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 0, cursor: "pointer", zIndex: 5,
        opacity: active ? 1 : 0.55,
        transition: "opacity .15s, border-color .15s",
        ...style,
      }}
    >+</button>
  );

  return (
    <div
      onClick={onSelect}
      onMouseEnter={() => onHover(node._uid)}
      onMouseLeave={() => onHover(null)}
      style={{
        position: "absolute", left: pos.x, top: pos.y,
        width: NODE_W, height: NODE_H, boxSizing: "border-box",
        display: "flex", background: "#fff", borderRadius: "12px",
        /*
         * While a connection is pending, "this is a valid target" outranks "this
         * is selected". A node whose panel happened to be open otherwise kept its
         * selection ring once an edge was started, so a legal target showed no
         * green outline and looked unconnectable.
         */
        border: `1px solid ${
          connectState === "valid" ? "#34d399"
            : connectState === "source" ? ACCENT
              : selected ? ACCENT
                : LINE
        }`,
        boxShadow: connectState === "valid"
          ? `${CARD_SHADOW}, 0 0 0 2px #6ee7b7`
          : connectState === "source" || selected
            ? `${CARD_SHADOW}, 0 0 0 2px #c7d2fe`
            : invalid ? `${CARD_SHADOW}, 0 0 0 2px #fca5a5`
              : CARD_SHADOW,
        opacity: invalid ? 0.5 : 1,
        cursor: invalid ? "not-allowed" : "pointer",
        transition: "border-color .15s, box-shadow .15s, opacity .15s",
        zIndex: active ? 4 : 2,
      }}
    >
      <span style={{ width: "3px", flexShrink: 0, borderRadius: "12px 0 0 12px", background: accent }} />
      <div style={{ flex: 1, minWidth: 0, padding: "9px 10px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <div style={{ fontSize: "11px", fontWeight: 600, color: INK, ...truncate }}>{node.name}</div>
        <div style={{ fontSize: "10px", color: MUTED, marginTop: "2px", ...truncate }}>{areaName}</div>
      </div>

      {issueCount > 0 && (
        <span style={{
          position: "absolute", top: "-7px", right: "-6px",
          background: "#fee2e2", color: "#b91c1c", borderRadius: "999px",
          fontSize: "9px", fontWeight: 700, padding: "1px 6px", zIndex: 5,
        }}>{issueCount}</span>
      )}

      {active && !connectState && (
        <button
          title="Remove node"
          onClick={e => { e.stopPropagation(); onRemove(); }}
          style={{
            position: "absolute", right: "6px", top: "4px",
            background: "none", border: "none", padding: 0, lineHeight: 1,
            fontSize: "13px", color: "#94a3b8", cursor: "pointer", zIndex: 5,
          }}
        >×</button>
      )}

      {!connectState && (
        <>
          {handle("top", { left: "50%", top: "-9px", transform: "translateX(-50%)" })}
          {handle("bottom", { left: "50%", bottom: "-9px", transform: "translateX(-50%)" })}
          {handle("left", { left: "-9px", top: "50%", transform: "translateY(-50%)" })}
          {handle("right", { right: "-9px", top: "50%", transform: "translateY(-50%)" })}
        </>
      )}
    </div>
  );
};

/**
 * Details of the hovered node. `placeHoverCard` has already chosen a side that
 * covers no node, no port and no edge label; it is click-through as well, so it
 * can never swallow a `+`.
 */
/**
 * Details of the hovered edge, in the same shape and placement language as the
 * node card so the canvas reads as one thing.
 *
 * Replaces the native `title` tooltip, which could not be styled, waited a
 * second to appear, and rendered its newlines as a cramped OS box that often
 * covered the very edge being described.
 */
const EdgeHoverCard = ({ rect, conditional, accent, selectorName, rule, from, to, targets }) => (
  <div style={{
    position: "absolute", left: rect.x, top: rect.y,
    width: rect.w, maxHeight: HOVER_CARD.maxH, boxSizing: "border-box",
    padding: "9px 11px", background: "#fff",
    border: `1px solid ${conditional ? `${accent}55` : LINE}`,
    borderLeft: `3px solid ${conditional ? accent : "#cbd5e1"}`,
    borderRadius: "10px", boxShadow: "0 4px 14px rgba(15,23,42,0.10)",
    overflowY: "auto", pointerEvents: "none", zIndex: 7,
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
      <span style={{
        fontSize: "9px", fontWeight: 800, letterSpacing: "0.04em",
        color: conditional ? accent : MUTED,
      }}>
        {conditional ? "CONDITIONAL" : "UNCONDITIONAL"}
      </span>
      {conditional && selectorName && (
        <span style={{
          fontSize: "9px", fontWeight: 700, color: "#fff", background: accent,
          borderRadius: "999px", padding: "2px 6px", lineHeight: 1.3,
        }}>{selectorName}</span>
      )}
    </div>

    <div style={{ fontSize: "11px", fontWeight: 600, color: INK, marginTop: "5px", lineHeight: 1.4 }}>
      {from} <span style={{ color: MUTED, fontWeight: 400 }}>→</span> {to}
    </div>

    <p style={{
      margin: "6px 0 0", fontSize: "10px", lineHeight: 1.45,
      color: conditional && !rule ? "#b45309" : "#475569",
      fontStyle: conditional && !rule ? "italic" : "normal",
      whiteSpace: "pre-wrap", overflowWrap: "anywhere",
    }}>
      {conditional
        ? (rule || "No rule set yet — click the edge to write one.")
        : "Always runs."}
    </p>

    {conditional && targets?.length > 1 && (
      <div style={{ fontSize: "9px", color: MUTED, marginTop: "6px", lineHeight: 1.5 }}>
        One decision over {targets.length}: {targets.join(", ")}
      </div>
    )}
  </div>
);

const HoverCard = ({ rect, title, role, area, description, descriptionFromLibrary }) => (
  <div style={{
    // height follows the content so the description is shown in full, capped so
    // one very long one cannot take over the canvas
    position: "absolute", left: rect.x, top: rect.y,
    width: HOVER_CARD.w, maxHeight: HOVER_CARD.maxH, boxSizing: "border-box",
    padding: "9px 11px", background: "#fff", border: `1px solid ${LINE}`,
    borderRadius: "10px", boxShadow: "0 4px 14px rgba(15,23,42,0.10)",
    // scroll rather than clip: a description longer than the cap stays reachable
    overflowY: "auto", pointerEvents: "none", zIndex: 6,
  }}>
    <div style={{ fontSize: "11px", fontWeight: 700, color: INK, ...truncate }}>{title}</div>
    {role && <div style={{ fontSize: "10px", color: ACCENT, marginTop: "2px", ...truncate }}>{role}</div>}
    {area && <div style={{ fontSize: "10px", color: MUTED, marginTop: "1px", ...truncate }}>{area}</div>}
    {description && (
      <>
        {descriptionFromLibrary && (
          <div style={{ fontSize: "9px", fontWeight: 700, color: MUTED, marginTop: "5px", letterSpacing: "0.03em" }}>
            FROM AGENT LIBRARY
          </div>
        )}
        <p style={{
          margin: descriptionFromLibrary ? "1px 0 0" : "5px 0 0",
          fontSize: "10px", color: "#475569", lineHeight: 1.45,
          whiteSpace: "pre-wrap", overflowWrap: "anywhere",
        }}>{description}</p>
      </>
    )}
  </div>
);

const MENU_W = 132, MENU_H = 78, MENU_OFFSET = 12;

/** Where a port menu sits relative to its node, per port. */
const MENU_PLACE = {
  top: (p) => ({ left: p.x + NODE_W / 2, top: p.y - MENU_OFFSET, transform: "translate(-50%, -100%)" }),
  bottom: (p) => ({ left: p.x + NODE_W / 2, top: p.y + NODE_H + MENU_OFFSET, transform: "translate(-50%, 0)" }),
  left: (p) => ({ left: p.x - MENU_OFFSET, top: p.y + NODE_H / 2, transform: "translate(-100%, -50%)" }),
  right: (p) => ({ left: p.x + NODE_W + MENU_OFFSET, top: p.y + NODE_H / 2, transform: "translate(0, -50%)" }),
};

/**
 * The two things a port can do. Opened by clicking a node's `+`, and flipped to
 * the opposite side when it would fall outside the canvas.
 */
const PortMenu = ({ pos, dir, canvasW, onAddNode, onAddEdge }) => {
  let side = dir;
  if (side === "left" && pos.x - MENU_OFFSET - MENU_W < 0) side = "right";
  else if (side === "right" && pos.x + NODE_W + MENU_OFFSET + MENU_W > canvasW) side = "left";
  else if (side === "top" && pos.y - MENU_OFFSET - MENU_H < 0) side = "bottom";

  return (
    <div
      onClick={e => e.stopPropagation()}
      style={{
        position: "absolute", ...MENU_PLACE[side](pos), width: MENU_W,
        background: "#fff", border: `1px solid ${LINE}`, borderRadius: "8px",
        boxShadow: "0 6px 16px rgba(15,23,42,0.14)", padding: "4px", zIndex: 9,
      }}
    >
      {[["Add node", onAddNode], ["Add edge", onAddEdge]].map(([label, fn]) => (
        <button
          key={label}
          onClick={fn}
          style={{
            display: "block", width: "100%", textAlign: "left",
            padding: "7px 9px", background: "transparent", border: "none",
            borderRadius: "6px", cursor: "pointer",
            fontSize: "12px", fontWeight: 600, color: "#334155",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = PL; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

// ─── main component ───────────────────────────────────────────────────────────

const WorkflowTemplates = ({ onClose }) => {

  // ── state ────────────────────────────────────────────────────────────────

  const [tab, setTab] = useState("list");
  /** The planner stays mounted once opened, so its session survives tab switches. */
  const [plannerSeen, setPlannerSeen] = useState(false);

  // list
  const [templates, setTemplates] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState(null);
  /**
   * Cursor paging for the templates list. A cursor API only hands out the way
   * *forward*, so stepping back means remembering where we have been: `trail`
   * holds the cursor that opened each page visited so far (page 1 opens with no
   * cursor, hence `[null]`), `pageIndex` points into it, and `nextCursor` is the
   * one the current page handed back — null when this is the last page.
   */
  const [trail, setTrail] = useState([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [nextCursor, setNextCursor] = useState(null);

  // agent library
  const [agentLibrary, setAgentLibrary] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentSearch, setAgentSearch] = useState("");
  const [collapsedCats, setCollapsedCats] = useState(new Set());
  /**
   * The library row being hovered, as `{ agent, top }` — `top` is the row's
   * viewport y, so the flyout describing it can line up with it.
   */
  const [libraryPeek, setLibraryPeek] = useState(null);

  // builder
  const [formMode, setFormMode] = useState("create");
  const [editingId, setEditingId] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");
  const [researchAreas, setResearchAreas] = useState([]);
  const [agentNodes, setAgentNodes] = useState([]);
  /** edges: { _uid, from, to, condition: null | { kind, expression } } — from/to are node _uids */
  const [edges, setEdges] = useState([]);
  const [synthName, setSynthName] = useState("Investment Synthesis");
  const [synthDesc, setSynthDesc] = useState("");

  // graph interaction
  /** null | { uid, mode: "from" | "to" } — drawing an edge out of / into `uid`. */
  const [connect, setConnect] = useState(null);
  const [hoverEdge, setHoverEdge] = useState(null);
  const [hoverNode, setHoverNode] = useState(null);
  /** null | { anchor, dir: "top" | "bottom" | "left" | "right" } */
  const [addIntent, setAddIntent] = useState(null);
  /** null | { anchor, dir } — the open port menu. */
  const [portMenu, setPortMenu] = useState(null);
  const [zoom, setZoom] = useState(1);
  const canvasRef = useRef(null);
  /** The right panel, so the library flyout can anchor to its left edge. */
  const panelRef = useRef(null);
  /** Previous layout, so an edit nudges the graph instead of re-flowing it. */
  const layoutHints = useRef(null);

  // right panel: "info" | "add" | "synth" | { nodeUid } | { edgeUid }
  const [rightPanel, setRightPanel] = useState("info");

  // save
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showIssues, setShowIssues] = useState(false);
  /**
   * Set when a save was held back because nodes are unconnected. Saving is not
   * blocked — the first click asks, the second goes through — so the flag clears
   * whenever the graph changes and the question has to be asked afresh.
   */
  const [unconnectedPrompt, setUnconnectedPrompt] = useState(null);

  // delete
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ── derived ──────────────────────────────────────────────────────────────

  const synthNodeId = useMemo(() => slugify(synthName || "synthesizer"), [synthName]);
  const layout = useMemo(() => {
    const next = computeLayout(agentNodes, edges, layoutHints.current);
    layoutHints.current = next.hints;   // idempotent: re-running with these hints is a fixed point
    return next;
  }, [agentNodes, edges]);

  const selectedNode = useMemo(() => {
    if (rightPanel && typeof rightPanel === "object" && rightPanel.nodeUid)
      return agentNodes.find(n => n._uid === rightPanel.nodeUid) || null;
    return null;
  }, [rightPanel, agentNodes]);

  const selectedEdge = useMemo(() => {
    if (rightPanel && typeof rightPanel === "object" && rightPanel.edgeUid)
      return edges.find(e => e._uid === rightPanel.edgeUid) || null;
    return null;
  }, [rightPanel, edges]);

  /**
   * The library agent's own description, by `agent_library_id`. Nodes store only
   * the id, so this is what lets the canvas show what an agent does without the
   * author having written a note for it.
   */
  const libraryById = useMemo(() => {
    const m = new Map();
    for (const a of agentLibrary) m.set(String(a.id), a);
    return m;
  }, [agentLibrary]);

  const libraryDescriptionOf = useCallback(
    (agentLibraryId) => libraryById.get(String(agentLibraryId))?.description?.trim() || null,
    [libraryById]
  );

  const labelOf = useCallback((nodeUid) => {
    if (nodeUid === SYNTH_UID) return synthName || "Synthesizer";
    return agentNodes.find(n => n._uid === nodeUid)?.name || nodeUid;
  }, [agentNodes, synthName]);

  const areaNameOf = useCallback(
    (gid) => researchAreas.find(r => r.group_id === gid)?.name || "No area assigned",
    [researchAreas]
  );

  /** Research area → accent colour, so parallel branches read apart at a glance. */
  const areaAccent = useCallback((gid) => {
    const palette = ["#4f46e5", "#0284c7", "#0f766e", "#b45309", "#475569"];
    const i = researchAreas.findIndex(r => r.group_id === gid);
    return palette[(i < 0 ? 0 : i) % palette.length];
  }, [researchAreas]);

  /** Synthesizer must keep at least one unconditional incoming edge. */
  const synthPlainEdges = useMemo(
    () => edges.filter(e => e.to === SYNTH_UID && !e.condition),
    [edges]
  );

  /**
   * Every selector in the graph, `selector_id` → its edges. A group of 2+ is one
   * decision choosing between several targets; a group of 1 is a simple gate.
   */
  const selectors = useMemo(() => selectorGroups(edges), [edges]);

  /**
   * Accent hue per selector, so two decisions fanning out of the same source do
   * not read as one group (§3, pattern E).
   */
  const selectorAccent = useCallback((selectorId) => {
    const palette = ["#b45309", "#be185d", "#0f766e", "#6d28d9", "#0369a1"];
    const i = [...selectors.keys()].indexOf(selectorId);
    return palette[(i < 0 ? 0 : i) % palette.length];
  }, [selectors]);


  /**
   * Human label for a selector — "Selector 1", numbered in the graph's own
   * order. The raw `selector_id` (`sel_1`, …) is what the saved plan carries and
   * what the backend groups on, but it is an implementation detail: it is never
   * shown to the author, only this label is.
   */
  const selectorLabel = useCallback((selectorId) => {
    const i = [...selectors.keys()].indexOf(selectorId);
    return i < 0 ? "Selector" : `Selector ${i + 1}`;
  }, [selectors]);

  /** "Selector 1 (2 targets)" — the label plus how many edges it decides between. */
  const selectorLabelWithCount = useCallback((selectorId) => {
    const n = (selectors.get(selectorId) || []).length;
    return `${selectorLabel(selectorId)} (${n} target${n === 1 ? "" : "s"})`;
  }, [selectors, selectorLabel]);

  /**
   * The ALWAYS / IF / PICK badge every edge list uses. A multi-target selector
   * reads `PICK n` so the list conveys "one decision, n candidates" the same way
   * the canvas does.
   */
  const EdgeKindChip = ({ edge }) => {
    const group = edge.condition ? (selectors.get(edge.condition.selector_id) || []) : [];
    if (!edge.condition)
      return <span style={{ ...chip, background: "#f1f5f9", color: MUTED }}>ALWAYS</span>;
    const accent = selectorAccent(edge.condition.selector_id);
    return (
      <span
        title={selectorLabel(edge.condition.selector_id)}
        style={{ ...chip, background: `${accent}1a`, color: accent }}
      >
        {group.length > 1 ? `PICK ${group.length}` : "IF"}
      </span>
    );
  };

  /**
   * Live validation. `list` gates saving; `warnings` never does — it flags
   * designs that are legal to build but risky at run time. `byNode` / `byEdge`
   * drive the inline badges.
   */
  const issues = useMemo(() => {
    const list = [], warnings = [], byNode = {}, byEdge = {};
    /** Names of nodes with no edges at all — the save handler confirms on these. */
    let isolatedNames = [];
    const forNode = (u, msg) => { (byNode[u] = byNode[u] || []).push(msg); list.push(msg); };
    /** Same inline badge as `forNode`, but the message never gates saving. */
    const warnNode = (u, msg) => { (byNode[u] = byNode[u] || []).push(msg); };
    const forEdge = (e, msg) => { (byEdge[e._uid] = byEdge[e._uid] || []).push(msg); list.push(msg); };
    const name = u => (u === SYNTH_UID ? (synthName || "Synthesizer") : agentNodes.find(n => n._uid === u)?.name || u);

    if (!templateName.trim()) list.push("Template name is required.");
    if (!templateDesc.trim()) list.push("Template description is required.");
    if (agentNodes.length === 0) list.push("Add at least one agent node — a template cannot be saved without one.");
    if (!synthName.trim()) list.push("Synthesizer name is required.");

    const areaIds = new Set(researchAreas.map(r => r.group_id));
    for (const n of agentNodes) {
      if (!n.group_id || !areaIds.has(n.group_id)) forNode(n._uid, `"${n.name}": assign a valid Research Area.`);
      if (!n.role?.trim()) forNode(n._uid, `"${n.name}": Role is required.`);
      if (!n.agent_library_id) forNode(n._uid, `"${n.name}": missing library agent reference.`);
    }

    if (layout.cyclic) list.push("The graph contains a cycle — remove the looping edge.");

    /**
     * Selector rules. A node may now have several conditional parents (it runs
     * if any selector picks it), so the only per-selector invariants are that
     * the rule is set and that a group really is one decision: the backend
     * guarantees every edge sharing a `selector_id` has the same source and the
     * same expression, so the builder must not emit a group that violates that.
     */
    const selectorOrder = [...selectorGroups(edges).keys()];
    for (const [selectorId, group] of selectorGroups(edges)) {
      // the raw id is never surfaced to the author — number it the same way the
      // panels and tooltips do
      const label = `Selector ${selectorOrder.indexOf(selectorId) + 1}`;
      /** One message, flagged on every edge of the group but listed once. */
      const forSelector = (msg) => {
        group.forEach((e, i) => (i === 0 ? forEdge(e, msg) : (byEdge[e._uid] = byEdge[e._uid] || []).push(msg)));
      };
      if (!group[0].condition.expression?.trim())
        forSelector(`${label} (from ${name(group[0].from)}) needs a rule.`);
      if (new Set(group.map(e => e.from)).size > 1)
        forSelector(`${label} fans out of more than one source — every edge in a selector must share the same source node.`);
      if (new Set(group.map(e => e.condition.expression || "")).size > 1)
        forSelector(`${label} has edges with different rules — a selector is one decision, so its edges share one rule.`);
    }

    const intoSynth = edges.filter(e => e.to === SYNTH_UID);
    if (agentNodes.length > 0) {
      if (intoSynth.length === 0) list.push("Connect at least one agent to the synthesizer.");
      else if (!intoSynth.some(e => !e.condition))
        /**
         * Every input to the synthesizer is selector-gated, so a run where no
         * selector picks anything reaches it with nothing to synthesize (an
         * empty memo). Allowed here so a template can be authored freely — but
         * note the API still rejects this shape, so saving surfaces a server
         * error; see the save handler.
         */
        warnings.push(
          "Every edge into the synthesizer is conditional. If no selector picks its target on a run, " +
          "the synthesizer runs with no inputs and the memo may come out empty."
        );
      /**
       * Nodes are added unwired, so "not connected yet" is the normal state
       * mid-build rather than a mistake. Flag it on the node and warn on save —
       * an isolated node (no edges at all) reads differently from one that is
       * wired but leads nowhere, so the two get their own messages.
       */
      const isolated = [], dangling = [];
      for (const n of agentNodes) {
        const wired = edges.some(e => e.from === n._uid || e.to === n._uid);
        if (!wired) {
          isolated.push(n);
          warnNode(n._uid, `"${n.name}" is not connected to any node — draw an edge from one of its ports.`);
        } else if (!reaches(n._uid, SYNTH_UID, edges)) {
          dangling.push(n);
          warnNode(n._uid, `"${n.name}" has no path to the synthesizer — its output would be dropped.`);
        }
      }
      const names = ns => ns.map(n => `"${n.name}"`).join(", ");
      isolatedNames = isolated.map(n => n.name);
      if (isolated.length)
        warnings.push(
          `${isolated.length === 1 ? "Node" : "Nodes"} ${names(isolated)} ${isolated.length === 1 ? "is" : "are"} `
          + "not connected to any node. Unconnected nodes never run — connect them, or remove them before saving."
        );
      if (dangling.length)
        warnings.push(
          `${names(dangling)} ${dangling.length === 1 ? "has" : "have"} no path to the synthesizer — `
          + `${dangling.length === 1 ? "its" : "their"} output would be dropped.`
        );
    }
    return { list, warnings, byNode, byEdge, isolated: isolatedNames };
  }, [templateName, templateDesc, synthName, agentNodes, researchAreas, edges, layout.cyclic]);

  /**
   * Search runs over `agentLibrary`, which `fetchAgentLibrary` fills with every
   * page of the cursor-paginated endpoint — so it spans the whole library, not
   * just what happens to be on screen. Every whitespace-separated term has to
   * match somewhere in the name or the category, so "risk credit" finds an
   * agent regardless of the order the words appear in.
   */
  const groupedAgents = useMemo(() => {
    const terms = agentSearch.toLowerCase().trim().split(/\s+/).filter(Boolean);
    const list = terms.length
      ? agentLibrary.filter(a => {
          const hay = `${a.name || ""} ${a.category || ""}`.toLowerCase();
          return terms.every(t => hay.includes(t));
        })
      : agentLibrary;
    const map = {};
    for (const a of list) {
      const cat = a.category || "Other";
      if (!map[cat]) map[cat] = [];
      map[cat].push(a);
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [agentLibrary, agentSearch]);

  // ── data fetching ─────────────────────────────────────────────────────────

  /**
   * Loads one page of templates. `cursor` is null for the first page and the
   * previous page's `nextCursor` thereafter. The response's cursor field is read
   * under both spellings so the paging holds whichever the API uses.
   */
  const loadTemplatePage = useCallback(async (cursor) => {
    setLoadingList(true); setListError(null);
    try {
      const res = await axios.get(endpoints.researchDagTemplates, {
        params: cursor ? { cursor } : {},
        headers: { ...getAuthHeaders(), Accept: "*/*" },
      });
      setTemplates(res.data?.templates || res.data?.data || []);
      setNextCursor(readNextCursor(res.data));
      return true;
    } catch (err) {
      setListError(getErrorMessage(err, "Failed to load templates"));
      return false;
    } finally { setLoadingList(false); }
  }, []);

  /** Reloads from page 1, discarding the trail — used after a create or delete. */
  const fetchTemplates = useCallback(async () => {
    const start = initialTrail();
    setTrail(start.trail);
    setPageIndex(start.pageIndex);
    await loadTemplatePage(null);
  }, [loadTemplatePage]);

  const goNextPage = useCallback(async () => {
    if (!canGoNext(nextCursor, loadingList)) return;
    const cursor = nextCursor;
    if (await loadTemplatePage(cursor)) {
      const moved = pushPage(trail, pageIndex, cursor);
      setTrail(moved.trail);
      setPageIndex(moved.pageIndex);
    }
  }, [nextCursor, loadingList, loadTemplatePage, trail, pageIndex]);

  const goPrevPage = useCallback(async () => {
    if (!canGoPrev(pageIndex, loadingList)) return;
    if (await loadTemplatePage(prevCursor(trail, pageIndex))) setPageIndex(i => i - 1);
  }, [pageIndex, loadingList, loadTemplatePage, trail]);

  /**
   * The whole library, not a page of it: the endpoint is cursor-paginated and
   * ignores `limit`/`page`, so we follow `nextCursor` until it comes back null.
   * A short page does *not* mean the end — only a null cursor does. Loading all
   * of it up front is what lets the search box below filter across every agent
   * rather than just the ones on screen.
   */
  const fetchAgentLibrary = useCallback(async () => {
    if (agentLibrary.length > 0) return;
    setLoadingAgents(true);
    try {
      const all = [];
      let cursor = null;
      for (;;) {
        const res = await axios.get(endpoints.agentsLibraryAdmin, {
          params: cursor ? { cursor } : {},
          headers: { ...getAuthHeaders(), Accept: "*/*" },
        });
        all.push(...(res.data?.data || []));
        cursor = res.data?.nextCursor || null;
        if (!cursor) break;
      }
      setAgentLibrary(all);
    } catch (err) {
      console.error("Agent library load failed", err);
    } finally { setLoadingAgents(false); }
  }, [agentLibrary.length]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);
  useEffect(() => { if (tab === "builder") fetchAgentLibrary(); }, [tab, fetchAgentLibrary]);

  // Esc backs out of an in-progress connection or an open port menu.
  useEffect(() => {
    if (!connect && !portMenu) return;
    const onKey = e => { if (e.key === "Escape") { setConnect(null); setPortMenu(null); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [connect, portMenu]);

  // ── builder helpers ───────────────────────────────────────────────────────

  const ensureArea = useCallback((category) => {
    const gid = slugify(category || "general");
    setResearchAreas(prev =>
      prev.some(r => r.group_id === gid) ? prev : [...prev, { group_id: gid, name: category || "General" }]
    );
    return gid;
  }, []);

  const openAdd = useCallback((intent) => {
    setAddIntent(intent);
    setConnect(null);
    setPortMenu(null);
    setRightPanel("add");
  }, []);

  /**
   * "Add edge" from a port. The top port draws an edge *into* the node (pick the
   * source); every other port draws one *out* of it (pick the target).
   */
  const startConnect = useCallback((anchor, dir) => {
    setPortMenu(null);
    setAddIntent(null);
    setConnect({ uid: anchor, mode: dir === "top" ? "to" : "from" });
  }, []);

  /**
   * Adds the node, and wires it to its anchor when the direction says how.
   *
   * Adding from a node's `+` is a statement about flow, so the edge that states
   * it is drawn too: a bottom add runs anchor → new, a top add runs new →
   * anchor. Leaving those unwired meant every add had to be followed by drawing
   * the very edge the gesture already implied.
   *
   * Left and right are the exception. They put the node on the anchor's own
   * level, which is what "runs in parallel with" means here, so there is no
   * edge to infer — a sideways edge would contradict the level it was just
   * placed on. Those stay unwired and are surfaced as a warning until connected.
   *
   * Every intent pins a layout row (see `placeNodeHints`); without it an unwired
   * node has no parents, so the layering would drop it on row 0 beside the entry
   * points rather than where the `+` was clicked.
   */
  const addAgent = useCallback((agent) => {
    const gid = ensureArea(agent.category || "General");
    const ids = new Set(agentNodes.map(n => n.node_id));
    const newNode = {
      _uid: uid(),
      node_id: makeNodeId(agent.name, ids),
      node_type: "agent",
      name: agent.name,
      agent_library_id: agent.id,
      group_id: gid,
      role: agent.name,
      description: "",
    };
    const intent = addIntent;
    const side = intent && (intent.dir === "left" || intent.dir === "right");

    // every intent pins a row, sideways included — the splice below only orders
    // the row it lands on, it cannot choose the row
    if (intent) {
      // child -> parents, so a node added above the top row can push the anchor's
      // own branch down without disturbing anything on another branch
      const parentsOf = {};
      for (const e of edges) (parentsOf[e.to] = parentsOf[e.to] || []).push(e.from);
      layoutHints.current = placeNodeHints(
        layoutHints.current, intent, newNode._uid, parentsOf,
      );
    }

    setAgentNodes(prev => {
      if (!side) return [...prev, newNode];
      const i = prev.findIndex(n => n._uid === intent.anchor);
      if (i < 0) return [...prev, newNode];
      const copy = [...prev];
      copy.splice(intent.dir === "left" ? i : i + 1, 0, newNode);
      return copy;
    });

    // the direction of the `+` is the direction of the flow
    if (intent && (intent.dir === "bottom" || intent.dir === "top")) {
      const from = intent.dir === "bottom" ? intent.anchor : newNode._uid;
      const to = intent.dir === "bottom" ? newNode._uid : intent.anchor;
      setEdges(prev => [...prev, { _uid: uid(), from, to, condition: null }]);
    }

    setAddIntent(null);
    setRightPanel({ nodeUid: newNode._uid });
  }, [agentNodes, ensureArea, addIntent, edges]);

  const updateNode = (nodeUid, field, val) =>
    setAgentNodes(p => p.map(n => n._uid === nodeUid ? { ...n, [field]: val } : n));

  const removeNode = (nodeUid) => {
    setAgentNodes(p => p.filter(n => n._uid !== nodeUid));
    setEdges(p => p.filter(e => e.from !== nodeUid && e.to !== nodeUid));
    if (connect?.uid === nodeUid) setConnect(null);
    if (addIntent?.anchor === nodeUid) setAddIntent(null);
    if (portMenu?.anchor === nodeUid) setPortMenu(null);
    setRightPanel("info");
  };

  /** A target is connectable if it isn't the source, isn't already linked, and won't create a cycle. */
  const canConnect = useCallback((from, to) => {
    if (!from || !to || from === to) return false;
    if (edges.some(e => e.from === from && e.to === to)) return false;
    if (reaches(to, from, edges)) return false;  // would close a loop
    return true;
  }, [edges]);

  /**
   * Completes the pending connection at `other`. Only ever appends an edge —
   * existing edges, and every node's row and column, are left as they are.
   */
  const completeConnect = (other) => {
    if (!connect) return;
    const from = connect.mode === "from" ? connect.uid : other;
    const to = connect.mode === "from" ? other : connect.uid;
    if (!canConnect(from, to)) { setConnect(null); return; }

    /*
     * Connecting two nodes on the same row lets the target drop a level.
     *
     * An edge means "runs after", and a row is a set of things that run
     * together, so a target on its source's own row is a contradiction: the
     * edge has to leave the source's bottom and climb back up into the target's
     * top, drawing backwards across the canvas. Holding the row kept the
     * picture still but produced exactly that shape.
     *
     * Letting the layering do its job instead moves the target one row down,
     * where the edge is a short vertical hop and the level tells the truth about
     * when the node runs. Nothing else on the canvas moves — every other node
     * keeps its row and its column.
     */
    const newEdge = { _uid: uid(), from, to, condition: null };
    setEdges(p => [...p, newEdge]);
    setConnect(null);
    setRightPanel({ edgeUid: newEdge._uid });
  };

  const removeEdge = (edgeUid) => {
    setEdges(p => p.filter(e => e._uid !== edgeUid));
    setRightPanel(rp => (rp && typeof rp === "object" && rp.edgeUid === edgeUid ? "info" : rp));
  };

  const setEdgeConditional = (edgeUid, on, joinSelectorId = null) =>
    setEdges(p => applyConditional(p, edgeUid, on, { joinSelectorId }));

  /** The rule belongs to the selector, so editing it updates the whole group. */
  const updateSelectorExpression = (selectorId, expression) =>
    setEdges(p => applySelectorExpression(p, selectorId, expression));

  /** Moves an edge between selectors; `null` opens a new one for it alone. */
  const moveEdgeToSelector = (edgeUid, selectorId) =>
    setEdges(p => applySelectorId(p, edgeUid, selectorId));

  const resetBuilder = () => {
    setFormMode("create"); setEditingId(null);
    setTemplateName(""); setTemplateDesc("");
    setResearchAreas([]); setAgentNodes([]); setEdges([]);
    setSynthName("Investment Synthesis"); setSynthDesc("");
    setRightPanel("info"); setShowIssues(false); setSaveError(null);
    setAgentSearch(""); setConnect(null); setHoverEdge(null); setUnconnectedPrompt(null);
    setHoverNode(null); setAddIntent(null); setPortMenu(null); setZoom(1);
    layoutHints.current = null;          // next graph gets a fresh layout
  };

  /**
   * Loads a saved template's plan — or a plan the AI planner produced — into the
   * builder. `mode`/`templateId` decide whether saving POSTs or PUTs.
   */
  const loadPlan = (rawPlan, { mode = "create", templateId = null } = {}) => {
    const plan = rawPlan || {};
    const allNodes = plan.nodes || [];
    const planEdges = plan.edges || [];
    const synth = allNodes.find(n => n.node_type === "synthesizer");
    const agents = allNodes.filter(n => n.node_type === "agent");

    const uidById = {};
    const loaded = agents.map(n => {
      const u = uid();
      uidById[n.node_id] = u;
      return {
        _uid: u,
        node_id: n.node_id, node_type: "agent", name: n.name,
        agent_library_id: n.agent_library_id || "",
        group_id: n.group_id || "",
        role: n.role || n.name,
        description: n.description || "",
      };
    });
    if (synth) uidById[synth.node_id] = SYNTH_UID;

    /**
     * Legacy plans stored `kind: "llm_router"` with no `selector_id` — each edge
     * was its own independent gate. Migrate them by minting one selector per
     * such edge, which preserves the old semantics exactly (a one-edge selector
     * IS a simple gate). Edges that already carry a `selector_id` keep it, so a
     * saved multi-target group survives a load/save round trip.
     */
    const legacySelectorIds = new Set(
      planEdges.map(e => e.condition?.selector_id).filter(Boolean)
    );
    const loadedEdges = planEdges
      .filter(e => uidById[e.from] && uidById[e.to])
      .map(e => {
        const c = e.condition;
        const conditional = c && (c.selector_id || c.expression || c.kind);
        if (!conditional) return { _uid: uid(), from: uidById[e.from], to: uidById[e.to], condition: null };
        let selectorId = c.selector_id;
        if (!selectorId) {
          selectorId = makeSelectorId(legacySelectorIds);
          legacySelectorIds.add(selectorId);
        }
        return {
          _uid: uid(),
          from: uidById[e.from], to: uidById[e.to],
          condition: { kind: CONDITION_KIND, selector_id: selectorId, expression: c.expression || "" },
        };
      });

    setFormMode(mode); setEditingId(mode === "edit" ? templateId : null);
    setTemplateName(plan.template?.name || "");
    setTemplateDesc(plan.template?.description || "");
    setResearchAreas([...(plan.research_areas || [])]);
    setAgentNodes(loaded);
    setEdges(loadedEdges);
    setSynthName(synth?.name || "Investment Synthesis");
    setSynthDesc(synth?.description || "");
    setRightPanel("info"); setShowIssues(false); setSaveError(null);
    setConnect(null); setHoverEdge(null); setHoverNode(null);
    setAddIntent(null); setPortMenu(null); setZoom(1);
    layoutHints.current = null;          // lay the loaded graph out from scratch
    setTab("builder");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startEdit = (tpl) => loadPlan(tpl.plan, { mode: "edit", templateId: tpl.template_id });

  /** The planner designs; the builder saves — so a proposed plan lands here unsaved. */
  const usePlannerPlan = (plan) => loadPlan(plan, { mode: "create" });

  // ── build & save ──────────────────────────────────────────────────────────

  const buildPlan = () => {
    const idOf = u => (u === SYNTH_UID ? synthNodeId : agentNodes.find(n => n._uid === u)?.node_id);
    // Persist agents in render order — the viewer derives x from `plan.nodes` order.
    const ordered = [
      ...layout.orderedUids.map(u => agentNodes.find(n => n._uid === u)).filter(Boolean),
      ...agentNodes.filter(n => !layout.orderedUids.includes(n._uid)),
    ];
    const nodes = [
      ...ordered.map(n => ({
        node_id: n.node_id, node_type: "agent", name: n.name,
        group_id: n.group_id, agent_library_id: n.agent_library_id, role: n.role,
        ...(n.description?.trim() ? { description: n.description.trim() } : {}),
      })),
      {
        node_id: synthNodeId, node_type: "synthesizer",
        name: synthName.trim(), synthesizer_kind: "investment_memo",
        ...(synthDesc.trim() ? { description: synthDesc.trim() } : {}),
      },
    ];
    const planEdges = edges.reduce((acc, e) => {
      const from = idOf(e.from), to = idOf(e.to);
      if (!from || !to) return acc;
      acc.push(e.condition
        ? {
            from, to,
            condition: {
              kind: CONDITION_KIND,
              selector_id: e.condition.selector_id,
              expression: (e.condition.expression || "").trim(),
            },
          }
        : { from, to });
      return acc;
    }, []);
    return {
      template: {
        name: templateName.trim(),
        description: templateDesc.trim(),
      },
      research_areas: researchAreas.filter(r => r.group_id && r.name),
      nodes,
      edges: planEdges,
    };
  };

  const handleSave = async () => {
    if (issues.list.length > 0) { setShowIssues(true); setRightPanel("info"); return; }
    /**
     * Unconnected nodes are legal but almost never intended, so the first click
     * on Create asks about them instead of saving. Clicking again with the same
     * set of nodes still unconnected saves anyway.
     */
    if (issues.isolated.length > 0 && unconnectedPrompt !== issues.isolated.join("\u0000")) {
      setUnconnectedPrompt(issues.isolated.join("\u0000"));
      setShowIssues(true);
      setRightPanel("info");
      return;
    }
    setSaving(true); setSaveError(null);
    try {
      const plan = buildPlan();
      if (formMode === "edit" && editingId) {
        await axios.put(urlResearchDagTemplate(editingId), { plan }, {
          headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        });
      } else {
        await axios.post(endpoints.researchDagTemplates, { plan }, {
          headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        });
      }
      resetBuilder(); setTab("list");
      await fetchTemplates();
    } catch (err) {
      /**
       * The API still enforces "the synthesizer needs one unconditional
       * incoming edge", which the builder now only warns about. Translate that
       * rejection into the builder's own vocabulary — the raw server text names
       * backend node ids the author never sees.
       */
      const raw = getErrorMessage(err, "Save failed");
      setSaveError(
        /unconditional incoming edge/i.test(raw)
          ? "The server rejected this template: the synthesizer needs at least one unconditional incoming edge. "
            + "Make one of its incoming edges unconditional and save again."
          : raw
      );
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(urlResearchDagTemplate(deleteTarget.template_id), {
        headers: getAuthHeaders(),
      });
      setDeleteTarget(null); await fetchTemplates();
    } catch (err) {
      alert(getErrorMessage(err, "Delete failed"));
    } finally { setDeleting(false); }
  };

  // ── right panel ───────────────────────────────────────────────────────────

  const panelInfo = () => (
    <div style={{ padding: "20px", overflowY: "auto" }}>
      <p style={{ fontSize: "11px", fontWeight: 700, color: P, margin: "0 0 14px", textTransform: "uppercase", letterSpacing: "0.06em" }}>
        Template Settings
      </p>

      <div style={{ marginBottom: "18px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
          <label style={{ ...lbl, marginBottom: 0 }}>Research Areas</label>
          <button
            style={{ fontSize: "11px", color: P, background: "none", border: "none", cursor: "pointer", padding: 0, textDecoration: "underline" }}
            onClick={() => setResearchAreas(p => [...p, { group_id: "", name: "" }])}
          >+ Add manually</button>
        </div>
        {researchAreas.length === 0 ? (
          <p style={{ fontSize: "11px", color: "#9ca3af", margin: 0, lineHeight: 1.5 }}>
            An area is just a name for a group of agents working together. One is created
            from the agent's category when you add it — rename it, delete it, or add your
            own here, then pick it on each node.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            {researchAreas.map((ra, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ width: "3px", height: "20px", borderRadius: "2px", background: areaAccent(ra.group_id), flexShrink: 0 }} />
                <input
                  style={{ ...inp, flex: 1, padding: "6px 8px", fontSize: "12px" }}
                  placeholder="Area name"
                  value={ra.name}
                  onChange={e => {
                    const name = e.target.value;
                    const newGid = slugify(name);
                    const oldGid = ra.group_id;
                    setResearchAreas(p => p.map((r, j) => j === i ? { group_id: newGid, name } : r));
                    setAgentNodes(p => p.map(n => n.group_id === oldGid ? { ...n, group_id: newGid } : n));
                  }}
                />
                <button
                  title={
                    agentNodes.some(n => n.group_id === ra.group_id)
                      ? `Used by ${agentNodes.filter(n => n.group_id === ra.group_id).length} node(s) — they will need a new area`
                      : "Remove this area"
                  }
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: "16px", padding: 0, lineHeight: 1, flexShrink: 0 }}
                  onClick={() => {
                    // Deleting an area must not leave nodes pointing at an id
                    // that no longer exists — that reads as "assign a valid
                    // Research Area" on save with no way to see which area went
                    // missing. Move its nodes to whichever area remains, or
                    // clear them so the node panel asks for a choice.
                    const gone = ra.group_id;
                    const rest = researchAreas.filter((_, j) => j !== i);
                    const fallback = rest[0]?.group_id || "";
                    setResearchAreas(rest);
                    setAgentNodes(p => p.map(n => n.group_id === gone ? { ...n, group_id: fallback } : n));
                  }}
                >×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {edges.length > 0 && (
        <div style={{ marginBottom: "18px" }}>
          <label style={lbl}>Edges ({edges.length})</label>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {edges.map(e => (
              <button
                key={e._uid}
                onClick={() => setRightPanel({ edgeUid: e._uid })}
                style={{
                  display: "flex", alignItems: "center", gap: "6px", textAlign: "left",
                  padding: "6px 8px", background: "#fff", border: `1px solid ${issues.byEdge[e._uid] ? "#fca5a5" : BORDER}`,
                  borderRadius: "6px", cursor: "pointer", fontSize: "11px", color: "#374151",
                }}
              >
                <EdgeKindChip edge={e} />
                <span style={{ flex: 1, minWidth: 0, ...truncate }}>
                  {labelOf(e.from)} → {labelOf(e.to)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: "14px" }}>
        {showIssues && issues.list.length > 0 && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "12px", padding: "10px 12px", marginBottom: "12px" }}>
            <p style={{ margin: "0 0 5px", fontWeight: 700, color: "#b91c1c", fontSize: "12px" }}>Fix before saving:</p>
            <ul style={{ margin: 0, paddingLeft: "16px" }}>
              {issues.list.map((e, i) => (
                <li key={i} style={{ fontSize: "11px", color: "#991b1b", marginBottom: "2px" }}>{e}</li>
              ))}
            </ul>
          </div>
        )}
        {/* A held-back save: the same click again goes through. */}
        {unconnectedPrompt && unconnectedPrompt === issues.isolated.join("\u0000") && (
          <div style={{ background: "#fffbeb", border: "1px solid #f59e0b", borderRadius: "12px", padding: "10px 12px", marginBottom: "12px" }}>
            <p style={{ margin: "0 0 5px", fontWeight: 700, color: "#92400e", fontSize: "12px" }}>
              {issues.isolated.length === 1 ? "This node is not connected" : "These nodes are not connected"}
            </p>
            <p style={{ margin: 0, fontSize: "11px", color: "#92400e", lineHeight: 1.5 }}>
              {issues.isolated.map(n => `"${n}"`).join(", ")} {issues.isolated.length === 1 ? "has" : "have"} no
              {" "}edges, so {issues.isolated.length === 1 ? "it" : "they"} will never run. Connect
              {" "}{issues.isolated.length === 1 ? "it" : "them"} from the node ports, or click
              {" "}{formMode === "edit" ? "Save Changes" : "Create Template"} again to save anyway.
            </p>
          </div>
        )}
        {/* warnings never gate saving, so they show as soon as they apply */}
        {issues.warnings.length > 0 && (
          <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: "12px", padding: "10px 12px", marginBottom: "12px" }}>
            <p style={{ margin: "0 0 5px", fontWeight: 700, color: "#92400e", fontSize: "12px" }}>Worth checking:</p>
            <ul style={{ margin: 0, paddingLeft: "16px" }}>
              {issues.warnings.map((w, i) => (
                <li key={i} style={{ fontSize: "11px", color: "#92400e", marginBottom: "2px", lineHeight: 1.5 }}>{w}</li>
              ))}
            </ul>
          </div>
        )}
        {saveError && (
          <p style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", padding: "8px 12px", fontSize: "12px", color: "#b91c1c", margin: "0 0 10px" }}>
            {saveError}
          </p>
        )}
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            style={{ flex: 1, padding: "9px", fontSize: "13px", fontWeight: 700, background: saving ? "#9ca3af" : P, color: "#fff", border: "none", borderRadius: "6px", cursor: saving ? "not-allowed" : "pointer" }}
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? "Saving…" : formMode === "edit" ? "Save Changes" : "Create Template"}
          </button>
          <button style={{ ...outBtn, padding: "9px 12px", flexShrink: 0 }} onClick={() => { resetBuilder(); setTab("list"); }}>Cancel</button>
        </div>
      </div>
    </div>
  );

  const panelAdd = () => {
    const matchCount = groupedAgents.reduce((n, [, list]) => n + list.length, 0);
    /**
     * The intent now only places the node on the canvas — nothing is wired for
     * the author — so the copy says where it lands and that connecting it is
     * their next step.
     */
    const relation = !addIntent
      ? "Dropped on the canvas unconnected — draw its edges from the node's ports."
      : addIntent.dir === "bottom"
        ? <>Placed <strong>below {labelOf(addIntent.anchor)}</strong>, unconnected — draw its edges from the ports.</>
        : addIntent.dir === "top"
          ? <>Placed <strong>above {labelOf(addIntent.anchor)}</strong>, unconnected — draw its edges from the ports.</>
          : <>Placed <strong>beside {labelOf(addIntent.anchor)}</strong>, unconnected — draw its edges from the ports.</>;

    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <div style={{ padding: "16px 20px 12px", borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
            <span style={{ fontWeight: 700, fontSize: "14px", color: INK }}>Add Agent</span>
            <button
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: "20px", color: "#6b7280", lineHeight: 1, padding: 0 }}
              onClick={() => { setAddIntent(null); setRightPanel("info"); }}
            >×</button>
          </div>
          <p style={{ margin: "0 0 10px", fontSize: "11px", color: MUTED, lineHeight: 1.5 }}>{relation}</p>
          <input
            style={inp}
            placeholder="Search agents…"
            value={agentSearch}
            onChange={e => setAgentSearch(e.target.value)}
          />
          {!loadingAgents && agentLibrary.length > 0 && (
            <p style={{ margin: "6px 0 0", fontSize: "10px", color: MUTED }}>
              {agentSearch.trim()
                ? `${matchCount} of ${agentLibrary.length} agents match`
                : `${agentLibrary.length} agents in the library`}
            </p>
          )}
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "10px 14px" }}>
          {loadingAgents ? (
            <p style={{ fontSize: "12px", color: "#9ca3af", textAlign: "center", padding: "24px 0" }}>Loading agents…</p>
          ) : groupedAgents.length === 0 ? (
            <p style={{ fontSize: "12px", color: "#9ca3af", textAlign: "center", padding: "24px 0" }}>No agents found.</p>
          ) : groupedAgents.map(([cat, agents]) => (
            <div key={cat} style={{ marginBottom: "6px" }}>
              <button
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 10px", background: "#f1f5f9", border: `1px solid ${LINE}`, borderRadius: "8px", cursor: "pointer", fontWeight: 600, fontSize: "12px", color: "#374151", marginBottom: "3px" }}
                onClick={() => setCollapsedCats(s => { const n = new Set(s); n.has(cat) ? n.delete(cat) : n.add(cat); return n; })}
              >
                <span>{cat}</span>
                <span style={{ fontSize: "10px" }}>{collapsedCats.has(cat) ? "▶" : "▼"}</span>
              </button>
              {!collapsedCats.has(cat) && agents.map(agent => (
                <div
                  key={agent.id}
                  onClick={() => addAgent(agent)}
                  onMouseEnter={e => setLibraryPeek({
                    agent,
                    top: e.currentTarget.getBoundingClientRect().top,
                  })}
                  onMouseLeave={() => setLibraryPeek(p => (p?.agent?.id === agent.id ? null : p))}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", background: libraryPeek?.agent?.id === agent.id ? "#f8fafc" : "#fff", border: `1px solid ${libraryPeek?.agent?.id === agent.id ? "#c7d2fe" : LINE}`, borderRadius: "8px", cursor: "pointer", marginBottom: "3px" }}
                >
                  <span style={{ fontWeight: 600, fontSize: "12px", color: INK, minWidth: 0, ...truncate }}>{agent.name}</span>
                  <span style={{ color: ACCENT, fontWeight: 700, fontSize: "18px", lineHeight: 1, flexShrink: 0, marginLeft: "6px" }}>+</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  /** One clickable edge, showing the node at the other end of it. */
  /**
   * One edge in a node's list. The row opens the edge's details; the `×` deletes
   * it outright, so any edge — including one drawn automatically when a node was
   * added — can be removed without first opening its panel.
   */
  const edgeRow = (e, dir) => (
    <div
      key={e._uid}
      style={{
        display: "flex", alignItems: "center", gap: "6px", width: "100%",
        padding: "6px 8px", background: "#fff", border: `1px solid ${issues.byEdge[e._uid] ? "#fca5a5" : LINE}`,
        borderRadius: "6px", fontSize: "11px", color: "#374151", marginBottom: "4px",
      }}
    >
      <button
        onClick={() => setRightPanel({ edgeUid: e._uid })}
        style={{
          flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: "6px",
          textAlign: "left", background: "none", border: "none", padding: 0,
          cursor: "pointer", fontSize: "11px", color: "inherit",
        }}
      >
        <span style={{ flex: 1, minWidth: 0, ...truncate, fontWeight: 600 }}>
          {labelOf(dir === "in" ? e.from : e.to)}
        </span>
        {!e.condition && <span style={{ ...chip, background: "#f1f5f9", color: MUTED }}>ALWAYS</span>}
      </button>
      <button
        title="Delete this edge"
        onClick={() => removeEdge(e._uid)}
        style={{
          flexShrink: 0, width: "18px", height: "18px", display: "flex",
          alignItems: "center", justifyContent: "center", lineHeight: 1, padding: 0,
          fontSize: "12px", background: "#fef2f2", color: "#b91c1c",
          border: "1px solid #fecaca", borderRadius: "50%", cursor: "pointer",
        }}
      >×</button>
    </div>
  );

  /**
   * A selector's edges shown as one block: the edges of a selector are a
   * single decision, so listing them separately (each repeating the same rule)
   * read as several unrelated gates. The rule is stated once, above the
   * targets it chooses between.
   */
  const selectorBlock = (selectorId, group, dir) => {
    const accent = selectorAccent(selectorId);
    const rule = group[0]?.condition?.expression?.trim();
    return (
      <div
        key={selectorId}
        style={{
          marginBottom: "6px", padding: "7px 9px", background: "#fff",
          border: `1px solid ${accent}33`, borderLeft: `3px solid ${accent}`, borderRadius: "8px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "3px" }}>
          <span style={{ fontSize: "10px", fontWeight: 800, color: accent }}>
            {selectorLabelWithCount(selectorId)}
          </span>
        </div>
        <p style={{
          margin: "0 0 5px", fontSize: "10px", lineHeight: 1.45,
          color: rule ? "#475569" : "#b45309", fontStyle: rule ? "normal" : "italic",
        }}>
          {rule || "No rule set yet — click a target below to write one."}
        </p>
        {group.map(e => edgeRow(e, dir))}
      </div>
    );
  };

  /**
   * Edges in one direction: the unconditional ones first as plain rows, then
   * one block per selector.
   */
  const edgeSection = (list, dir, emptyText) => {
    if (list.length === 0) {
      return <p style={{ margin: "0 0 6px", fontSize: "11px", color: MUTED, lineHeight: 1.5 }}>{emptyText}</p>;
    }
    const plain = list.filter(e => !e.condition);
    const grouped = new Map();
    for (const e of list) {
      if (!e.condition) continue;
      const id = e.condition.selector_id;
      if (!grouped.has(id)) grouped.set(id, []);
      grouped.get(id).push(e);
    }
    return (
      <>
        {plain.map(e => edgeRow(e, dir))}
        {[...grouped.entries()].map(([id, group]) => selectorBlock(id, group, dir))}
      </>
    );
  };

  const panelNodeConfig = (node) => {
    const incoming = edges.filter(e => e.to === node._uid);
    const outgoing = edges.filter(e => e.from === node._uid);
    const nodeIssues = issues.byNode[node._uid] || [];
    return (
      <div style={{ padding: "20px", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "16px" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: "15px", color: INK }}>{node.name}</div>
          </div>
          <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: "20px", color: "#6b7280", lineHeight: 1, padding: 0 }} onClick={() => setRightPanel("info")}>×</button>
        </div>

        {nodeIssues.length > 0 && (
          <ul style={{ margin: "0 0 14px", padding: "8px 12px 8px 26px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "12px" }}>
            {nodeIssues.map((m, i) => (
              <li key={i} style={{ fontSize: "11px", color: "#b91c1c", lineHeight: 1.5 }}>{m}</li>
            ))}
          </ul>
        )}

        <div style={{ marginBottom: "12px" }}>
          <label style={lbl}>Role *</label>
          <input
            style={inp}
            placeholder="e.g. Corporate Governance Analyst"
            value={node.role}
            onChange={e => updateNode(node._uid, "role", e.target.value)}
          />
        </div>

        <div style={{ marginBottom: "12px" }}>
          <label style={lbl}>Research Area *</label>
          <select
            style={{ ...inp, cursor: "pointer" }}
            value={node.group_id}
            onChange={e => updateNode(node._uid, "group_id", e.target.value)}
          >
            <option value="">— Select area —</option>
            {researchAreas.map(ra => (
              <option key={ra.group_id} value={ra.group_id}>{ra.name}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label style={lbl}>Description (optional)</label>
          <input
            style={inp}
            placeholder="What does this agent do here?"
            value={node.description}
            onChange={e => updateNode(node._uid, "description", e.target.value)}
          />
        </div>

        <div style={{ marginBottom: "16px", padding: "12px", background: "#f8fafc", border: `1px solid ${LINE}`, borderRadius: "12px" }}>
          <label style={{ ...lbl, marginBottom: "6px" }}>Receives from</label>
          {edgeSection(
            incoming, "in",
            "Nothing feeds this node — it starts the workflow and runs in parallel with the other starting nodes.",
          )}

          <label style={{ ...lbl, marginBottom: "6px", marginTop: "12px" }}>Feeds into</label>
          {edgeSection(
            outgoing, "out",
            "This node's output goes nowhere yet — draw an edge from one of its ports.",
          )}

          <div style={{ display: "flex", gap: "6px", marginTop: "8px" }}>
            <button
              style={{ ...outBtn, flex: 1, padding: "6px 8px", fontSize: "11px" }}
              onClick={() => { startConnect(node._uid, "bottom"); setRightPanel("info"); }}
            >Add edge from here</button>
            <button
              style={{ ...outBtn, flex: 1, padding: "6px 8px", fontSize: "11px" }}
              onClick={() => openAdd({ anchor: node._uid, dir: "bottom" })}
            >Add agent after</button>
          </div>
        </div>

        <button
          style={{ width: "100%", padding: "8px", fontSize: "12px", fontWeight: 600, background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca", borderRadius: "8px", cursor: "pointer" }}
          onClick={() => removeNode(node._uid)}
        >
          Remove agent from workflow
        </button>
      </div>
    );
  };

  const panelEdgeConfig = (edge) => {
    /**
     * Gating the synthesizer's last unconditional edge is allowed — it just
     * means a run can reach the memo with no inputs, which is a warning, not an
     * error. Flagged inline so the author sees the consequence at the moment
     * they make the change.
     */
    const lastPlainIntoSynth =
      edge.to === SYNTH_UID && !edge.condition && synthPlainEdges.length <= 1;
    const selectorId = edge.condition?.selector_id || null;
    const group = selectorId ? (selectors.get(selectorId) || []) : [];
    const siblings = group.filter(e => e._uid !== edge._uid);
    /** Selectors already fanning out of this edge's source — this edge can join one. */
    const joinable = selectorsFrom(edges, edge.from).filter(id => id !== selectorId);
    const accent = selectorId ? selectorAccent(selectorId) : "#b45309";

    return (
      <div style={{ padding: "20px", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "14px" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: "15px", color: INK }}>Edge</div>
            <div style={{ fontSize: "11px", color: MUTED, marginTop: "3px" }}>
              {labelOf(edge.from)} <span style={{ color: ACCENT, fontWeight: 700 }}>→</span> {labelOf(edge.to)}
            </div>
          </div>
          <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: "20px", color: "#6b7280", lineHeight: 1, padding: 0 }} onClick={() => setRightPanel("info")}>×</button>
        </div>

        <label style={lbl}>Edge type</label>
        <div style={{ display: "flex", gap: "6px", marginBottom: "12px" }}>
          {[
            { on: false, title: "Unconditional", sub: "Always proceed" },
            { on: true, title: "Conditional", sub: "A selector picks it" },
          ].map(opt => {
            const active = !!edge.condition === opt.on;
            return (
              <button
                key={String(opt.on)}
                onClick={() => setEdgeConditional(edge._uid, opt.on)}
                style={{
                  flex: 1, padding: "9px 8px", borderRadius: "10px", textAlign: "left",
                  background: active ? PL : "#fff",
                  border: `2px solid ${active ? ACCENT : LINE}`,
                  cursor: "pointer",
                }}
              >
                <div style={{ fontSize: "12px", fontWeight: 700, color: active ? ACCENT : "#374151" }}>{opt.title}</div>
                <div style={{ fontSize: "10px", color: MUTED, marginTop: "1px" }}>{opt.sub}</div>
              </button>
            );
          })}
        </div>

        {lastPlainIntoSynth && (
          <p style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: "8px", padding: "8px 10px", fontSize: "11px", color: "#92400e", margin: "0 0 12px", lineHeight: 1.5 }}>
            This is the only unconditional edge into the synthesizer. Gating it means a run where no selector
            picks anything reaches the memo with <strong>no inputs</strong>.
          </p>
        )}

        {edge.condition && (
          <>
            <div style={{ marginBottom: "12px" }}>
              <label style={lbl}>Selector ID</label>
              <select
                style={{ ...inp, cursor: "pointer" }}
                value={selectorId}
                onChange={e => moveEdgeToSelector(edge._uid, e.target.value === "__new__" ? null : e.target.value)}
              >
                <option value={selectorId}>{selectorLabelWithCount(selectorId)}</option>
                {joinable.map(id => (
                  <option key={id} value={id}>Join {selectorLabelWithCount(id)}</option>
                ))}
                {/* hidden when the edge is already its own decision — moving it
                    to a new selector would then change nothing */}
                {group.length > 1 && (
                  <option value="__new__">New selector (its own decision)</option>
                )}
              </select>
              <p style={{ fontSize: "11px", color: MUTED, margin: "5px 0 0", lineHeight: 1.5 }}>
                {CONDITION_KIND_HINT}
              </p>
            </div>

            {siblings.length > 0 && (
              <div style={{ marginBottom: "12px", background: "#fffbeb", border: `1px solid ${accent}33`, borderLeft: `3px solid ${accent}`, borderRadius: "8px", padding: "9px 11px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: accent, marginBottom: "4px" }}>
                  One decision, {group.length} candidates
                </div>
                <div style={{ fontSize: "11px", color: "#92400e", lineHeight: 1.6 }}>
                  {group.map(e => labelOf(e.to)).join(" · ")}
                </div>
                <p style={{ fontSize: "10px", color: "#a16207", margin: "6px 0 0", lineHeight: 1.5 }}>
                  The selector may pick <strong>any subset</strong> — all, some, or none of them. Unpicked
                  targets are skipped for that run.
                </p>
              </div>
            )}

            <div style={{ marginBottom: "14px" }}>
              <label style={lbl}>Selector rule *</label>
              <textarea
                style={{ ...inp, minHeight: "72px", resize: "vertical", fontSize: "12px" }}
                placeholder="e.g. Pick the follow-ups the earnings findings warrant"
                value={edge.condition.expression}
                onChange={e => updateSelectorExpression(selectorId, e.target.value)}
              />
              <p style={{ fontSize: "11px", color: MUTED, margin: "5px 0 0", lineHeight: 1.5 }}>
                {siblings.length > 0
                  ? <>The rule belongs to the selector, so this edits <strong>all {group.length} edges</strong> in it.</>
                  : <>If the selector does not pick it, <strong>{labelOf(edge.to)}</strong> is skipped for that run.</>}
              </p>
            </div>
          </>
        )}

        <button
          style={{ width: "100%", padding: "8px", fontSize: "12px", fontWeight: 600, background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca", borderRadius: "8px", cursor: "pointer" }}
          onClick={() => removeEdge(edge._uid)}
        >
          Delete edge
        </button>
      </div>
    );
  };

  const panelSynth = () => (
    <div style={{ padding: "20px", overflowY: "auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: "15px", color: "#4c1d95" }}>✦ Synthesizer</div>
          <div style={{ fontSize: "11px", color: SYNTH_ACCENT, marginTop: "2px" }}>Final output</div>
        </div>
        <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: "20px", color: "#6b7280", lineHeight: 1, padding: 0 }} onClick={() => setRightPanel("info")}>×</button>
      </div>
      <div style={{ marginBottom: "12px" }}>
        <label style={lbl}>Name *</label>
        <input style={inp} value={synthName} placeholder="e.g. Investment Synthesis" onChange={e => setSynthName(e.target.value)} />
      </div>
      <div style={{ marginBottom: "14px" }}>
        <label style={lbl}>Description (optional)</label>
        <input style={inp} value={synthDesc} placeholder="What this memo covers" onChange={e => setSynthDesc(e.target.value)} />
      </div>
      <div style={{ marginBottom: "14px" }}>
        <label style={lbl}>Receives from</label>
        {edgeSection(
          edges.filter(e => e.to === SYNTH_UID), "in",
          "Nothing feeds the synthesizer yet — connect at least one agent.",
        )}
      </div>
      <p style={{ fontSize: "11px", color: MUTED, lineHeight: 1.6, margin: 0 }}>
        The synthesizer always runs last, and runs on every run — a conditional edge into it means <em>that
        input</em> may or may not be included, never that the memo is skipped. If <strong>every</strong> incoming
        edge is conditional, a run where no selector picks anything reaches it with no inputs and the memo may
        come out empty. Every workflow has exactly one synthesizer.
      </p>
    </div>
  );

  /**
   * The full description of the library agent being hovered, shown beside the
   * list rather than inside it.
   *
   * The list already lives in the right-hand panel, so this opens to its *left*,
   * over the canvas — opening further right would run off the screen. It is
   * `position: fixed` to escape the panel's `overflow: hidden`, and vertically
   * aligned to the row it describes, clamped to stay on screen.
   */
  const agentDescriptionFlyout = () => {
    const { agent, top } = libraryPeek;
    const description = agent?.description?.trim();
    const W = 300;
    const panel = panelRef.current?.getBoundingClientRect();
    const left = Math.max(12, (panel ? panel.left : window.innerWidth - 420) - W - 12);
    const clampedTop = Math.min(Math.max(12, top), Math.max(12, window.innerHeight - 220));

    return (
      <div
        style={{
          position: "fixed", left, top: clampedTop, width: W, maxHeight: "40vh",
          overflowY: "auto", boxSizing: "border-box", padding: "11px 13px",
          background: "#fff", border: `1px solid ${LINE}`, borderRadius: "10px",
          boxShadow: "0 8px 24px rgba(15,23,42,0.14)", pointerEvents: "none", zIndex: 40,
        }}
      >
        <div style={{ fontSize: "12px", fontWeight: 700, color: INK }}>{agent.name}</div>
        {agent.category && (
          <div style={{ fontSize: "10px", color: ACCENT, marginTop: "2px" }}>{agent.category}</div>
        )}
        <p style={{
          margin: "7px 0 0", fontSize: "11px", lineHeight: 1.5,
          color: description ? "#475569" : "#9ca3af",
          fontStyle: description ? "normal" : "italic",
          whiteSpace: "pre-wrap", overflowWrap: "anywhere",
        }}>
          {description || "No description for this agent."}
        </p>
      </div>
    );
  };

  const renderRightPanel = () => {
    if (rightPanel === "add") return panelAdd();
    if (rightPanel === "synth") return panelSynth();
    if (selectedEdge) return panelEdgeConfig(selectedEdge);
    if (selectedNode) return panelNodeConfig(selectedNode);
    return panelInfo();
  };

  // ── canvas ────────────────────────────────────────────────────────────────

  /** "source" = the node the pending edge is anchored to; "valid" = a legal other end. */
  const connectStateOf = (nodeUid) => {
    if (!connect) return null;
    if (connect.uid === nodeUid) return "source";
    const ok = connect.mode === "from"
      ? canConnect(connect.uid, nodeUid)
      : canConnect(nodeUid, connect.uid);
    return ok ? "valid" : "invalid";
  };

  /** The node whose edges are highlighted: selection first, then hover. */
  const activeNodeUid = hoverNode
    || selectedNode?._uid
    || (rightPanel === "synth" ? SYNTH_UID : null);

  /** Boxes the floating layers must keep clear of: every node, plus the terminals. */
  /**
   * Node rectangles keyed by uid, so an edge can be routed around every node
   * except the two it joins. The terminals are in here too: a long edge that
   * skips rows can otherwise sail straight through START or END.
   */
  const boxesByUid = useMemo(() => {
    const m = {};
    for (const [u, p] of Object.entries(layout.pos)) m[u] = { x: p.x, y: p.y, w: NODE_W, h: NODE_H };
    m.__start__ = { x: layout.start.x, y: layout.start.y, w: TERMINAL, h: TERMINAL };
    m.__end__ = { x: layout.end.x, y: layout.end.y, w: TERMINAL, h: TERMINAL };
    return m;
  }, [layout]);

  /**
   * One path per edge, routed clear of the nodes in between. Memoised on the
   * geometry rather than recomputed per render: hovering an edge re-renders the
   * canvas, and the routes must not shift under the cursor.
   */
  const edgeRoutes = useMemo(() => {
    const { pos } = layout;
    const out = {};
    for (const e of edges) {
      const a = pos[e.from], b = pos[e.to];
      if (!a || !b) continue;
      /*
       * The faces to leave and enter are chosen from where the nodes actually
       * sit, rather than always bottom-to-top. Two nodes side by side on one row
       * are joined across the gap between them; an edge drawn from a left or
       * right port no longer dives out of the bottom of its source and loops
       * back up into the top of its target.
       */
      const { x1, y1, x2, y2 } = edgeEndpoints(
        { x: a.x, y: a.y, w: NODE_W, h: NODE_H },
        { x: b.x, y: b.y, w: NODE_W, h: NODE_H },
      );
      // keep the endpoints alongside the path: the hover card needs to sample
      // the same route to treat it as an obstacle
      out[e._uid] = {
        ...routeEdge(x1, y1, x2, y2, obstaclesFor(boxesByUid, e.from, e.to)),
        x1, y1, x2, y2,
      };
    }
    return out;
  }, [edges, layout, boxesByUid]);

  /**
   * The drawn edges as obstacle rects, so the hover card can be kept off the
   * connections and not just off the node boxes. Sampled from the very routes
   * being rendered, so a detoured edge is avoided where it actually runs rather
   * than where a straight line would have gone.
   */
  const edgePolylines = useMemo(() => {
    const polylines = Object.values(edgeRoutes).map(r => (
      r.laneX != null
        ? sampleLane(r.x1, r.y1, r.x2, r.y2, r.laneX)
        : sampleCubic(r.x1, r.y1, r.x2, r.y2, r.bowX)
    ));

    // the START and END arrows are drawn outside `edges`, but hide just as badly
    const { pos } = layout;
    for (const u of layout.roots) {
      if (!pos[u]) continue;
      polylines.push(sampleCubic(
        layout.start.x + TERMINAL / 2, layout.start.y + TERMINAL,
        pos[u].x + NODE_W / 2, pos[u].y, null,
      ));
    }
    if (pos[SYNTH_UID]) {
      polylines.push(sampleCubic(
        pos[SYNTH_UID].x + NODE_W / 2, pos[SYNTH_UID].y + NODE_H,
        layout.end.x + TERMINAL / 2, layout.end.y, null,
      ));
    }
    return polylines;
  }, [edgeRoutes, layout]);

  const edgeHoverObstacles = useMemo(() => edgeObstacles(edgePolylines), [edgePolylines]);

  /**
   * How far the drawing actually reaches, edges included.
   *
   * `layout.width/height` bound the *nodes* only. A routed edge deliberately
   * detours past the widest row to get around it, so an SVG sized to the layout
   * clipped those edges at its viewport — they vanished mid-curve and picked up
   * again further along, reading as one edge broken into two pieces. Zooming out
   * did not help, because the clip is in layout coordinates, not screen ones.
   *
   * `padLeft` covers detours that run to a negative x. The SVG is widened and
   * pulled left by that amount, and given a matching `viewBox` origin, so the
   * off-canvas stretch is inside the viewport while every path keeps its
   * original coordinates — nothing else on the canvas has to move.
   */
  const drawBox = useMemo(() => {
    const M = 14;   // a little air so a stroke is not flush against the edge
    let minX = 0, maxX = layout.width, maxY = layout.height;
    for (const pts of edgePolylines) {
      for (const p of pts) {
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
        if (p.y > maxY) maxY = p.y;
      }
    }
    const padLeft = minX < 0 ? Math.ceil(-minX) + M : 0;
    return {
      padLeft,
      width: Math.ceil(maxX) + padLeft + M,
      height: Math.ceil(maxY) + M,
    };
  }, [edgePolylines, layout.width, layout.height]);

  const nodeBoxes = useMemo(() => [
    ...Object.values(layout.pos).map(p => ({ x: p.x, y: p.y, w: NODE_W, h: NODE_H })),
    { x: layout.start.x, y: layout.start.y, w: TERMINAL, h: TERMINAL },
    { x: layout.end.x, y: layout.end.y, w: TERMINAL, h: TERMINAL },
  ], [layout]);

  /** Details of the hovered node, on a side that covers nothing. */
  const hoverCard = useMemo(() => {
    if (!hoverNode || connect || portMenu) return null;
    const pos = layout.pos[hoverNode];
    if (!pos) return null;
    const synth = hoverNode === SYNTH_UID;
    const node = synth ? null : agentNodes.find(n => n._uid === hoverNode);
    if (!synth && !node) return null;

    // the graph is centred in the scroll box, so a narrow graph leaves empty
    // canvas on both sides — the card may use it rather than cover a terminal
    const slack = Math.max(0, ((canvasRef.current?.clientWidth || 0) / zoom - layout.width) / 2 - 8);

    const role = synth ? "Final synthesizer" : node.role;
    const area = synth ? null : areaNameOf(node.group_id);
    const description = (synth ? synthDesc : node.description)?.trim()
      || (synth ? null : libraryDescriptionOf(node.agent_library_id));
    const descriptionFromLibrary = !synth
      && !node.description?.trim()
      && !!libraryDescriptionOf(node.agent_library_id);

    return {
      /*
       * Placed against the edges as well as the nodes. Node boxes alone were not
       * enough: edges leave a node's top and bottom, so a card that cleared every
       * box could still land squarely on the connections.
       *
       * The height is measured from the content rather than fixed, because the
       * card now shows the description in full — a taller card has to be placed
       * against the space it will actually take up.
       */
      rect: placeHoverCard(
        { x: pos.x, y: pos.y, w: NODE_W, h: NODE_H },
        [...nodeBoxes.map(b => inflate(b, PORT_HALO)), ...edgeHoverObstacles],
        { x: -slack, w: layout.width + slack * 2, h: layout.height },
        hoverCardHeight(description, {
          hasRole: !!role, hasArea: !!area, fromLibrary: descriptionFromLibrary,
        }),
      ),
      title: synth ? `✦ ${synthName || "Synthesizer"}` : node.name,
      role,
      area,
      /**
       * The author's own note for this node if they wrote one; otherwise the
       * library agent's description, so hovering a node explains what the agent
       * actually does without a trip to the agent library. The two are labelled
       * apart because one is instruction and the other is reference.
       */
      description,
      descriptionFromLibrary,
    };
  }, [
    hoverNode, connect, portMenu, layout, agentNodes,
    nodeBoxes, edgeHoverObstacles, synthName, synthDesc, areaNameOf, zoom,
    libraryDescriptionOf,
  ]);

  /**
   * The hovered edge's card, placed clear of the graph.
   *
   * Anchored to the midpoint of the route *actually drawn* — a detoured edge
   * does not follow the straight line between its nodes, so the naive midpoint
   * would put the card nowhere near it. From there it goes through the same
   * placement as the node card, against both the nodes and the other edges, so
   * it never lands on the connection it is describing.
   */
  const edgeCard = useMemo(() => {
    if (!hoverEdge || connect || portMenu) return null;
    const e = edges.find(x => x._uid === hoverEdge);
    const r = e && edgeRoutes[e._uid];
    if (!e || !r) return null;

    const pts = r.laneX != null
      ? sampleLane(r.x1, r.y1, r.x2, r.y2, r.laneX)
      : sampleCubic(r.x1, r.y1, r.x2, r.y2, r.bowX);
    const mid = pts[Math.floor(pts.length / 2)] || { x: r.x1, y: r.y1 };

    const group = e.condition ? (selectors.get(e.condition.selector_id) || []) : [];
    const rule = e.condition?.expression?.trim() || null;
    const W = 248;
    const h = hoverCardHeight(rule || "Always runs.", {
      hasRole: true, hasArea: group.length > 1,
    });

    // a near-zero box at the midpoint: the card is placed *beside* that point on
    // the curve, so it never covers the stretch of edge it describes
    const rect = placeHoverCard(
      { x: mid.x - 1, y: mid.y - 1, w: 2, h: 2 },
      [...nodeBoxes.map(b => inflate(b, PORT_HALO)), ...edgeHoverObstacles],
      { x: -drawBox.padLeft, w: drawBox.width, h: drawBox.height },
      h,
    );

    return {
      rect: { ...rect, w: W },
      conditional: !!e.condition,
      accent: e.condition ? selectorAccent(e.condition.selector_id) : MUTED,
      selectorName: e.condition ? selectorLabel(e.condition.selector_id) : null,
      rule,
      from: labelOf(e.from),
      to: labelOf(e.to),
      targets: group.length > 1 ? group.map(g => labelOf(g.to)) : null,
    };
  }, [
    hoverEdge, connect, portMenu, edges, edgeRoutes, selectors, selectorAccent,
    selectorLabel, labelOf, nodeBoxes, edgeHoverObstacles, drawBox,
  ]);

  const fitToWidth = () => {
    const box = canvasRef.current;
    if (!box) return;
    // fit the whole drawing, edges included, or the detoured ones stay off-screen
    setZoom(clampZoom((box.clientWidth - 24) / drawBox.width));
  };

  const renderEdges = () => {
    const { pos } = layout;
    const drawn = edges.filter(e => pos[e.from] && pos[e.to]);
    const anyFocus = !!activeNodeUid || !!hoverEdge || !!selectedEdge;

    const isHot = (e) =>
      hoverEdge === e._uid
      || selectedEdge?._uid === e._uid
      || (!!activeNodeUid && (e.from === activeNodeUid || e.to === activeNodeUid));

    const terminalHot = (nodeUid) => !!activeNodeUid && activeNodeUid === nodeUid;

    const strokeFor = (hot) => ({
      stroke: hot ? EDGE_HOT : EDGE_IDLE,
      strokeWidth: hot ? 2.25 : 1.35,
      opacity: anyFocus && !hot ? EDGE_DIM_OPACITY : 1,
      markerEnd: `url(#${hot ? "wf-arrow-hot" : "wf-arrow"})`,
    });

    return (
      <>
        {/*
          Sized and offset to the whole drawing, not just the node bounds, so an
          edge detouring past either side stays inside the viewport instead of
          being clipped mid-curve. The `viewBox` origin absorbs the left pad, so
          every path keeps its layout coordinates and nothing else has to move.
        */}
        <svg
          width={drawBox.width} height={drawBox.height}
          viewBox={`${-drawBox.padLeft} 0 ${drawBox.width} ${drawBox.height}`}
          style={{
            position: "absolute", top: 0, left: -drawBox.padLeft,
            pointerEvents: "none", zIndex: 1, overflow: "visible",
          }}
        >
          <defs>
            {[["wf-arrow", EDGE_IDLE], ["wf-arrow-hot", EDGE_HOT]].map(([id, c]) => (
              <marker key={id} id={id} viewBox="0 0 8 8" refX="7" refY="4"
                markerWidth="8" markerHeight="8" orient="auto-start-reverse">
                <path d="M 0 0 L 8 4 L 0 8 z" fill={c} />
              </marker>
            ))}
          </defs>

          {/* START → root nodes, routed clear of anything in between */}
          {layout.roots.filter(u => pos[u]).map(u => (
            <path
              key={`s-${u}`}
              d={routeEdge(
                layout.start.x + TERMINAL / 2, layout.start.y + TERMINAL,
                pos[u].x + NODE_W / 2, pos[u].y,
                obstaclesFor(boxesByUid, "__start__", u),
              ).d}
              fill="none" {...strokeFor(terminalHot(u))}
            />
          ))}

          {/* synthesizer → END */}
          {pos[SYNTH_UID] && (
            <path
              d={routeEdge(
                pos[SYNTH_UID].x + NODE_W / 2, pos[SYNTH_UID].y + NODE_H,
                layout.end.x + TERMINAL / 2, layout.end.y,
                obstaclesFor(boxesByUid, SYNTH_UID, "__end__"),
              ).d}
              fill="none" {...strokeFor(terminalHot(SYNTH_UID))}
            />
          )}

          {drawn.map(e => {
            const a = pos[e.from], b = pos[e.to];
            const d = edgeRoutes[e._uid]?.d
              ?? edgePath(a.x + NODE_W / 2, a.y + NODE_H, b.x + NODE_W / 2, b.y);
            const hot = isHot(e);
            return (
              <g key={e._uid}>
                <path
                  d={d} fill="none" stroke="transparent" strokeWidth="16"
                  style={{ pointerEvents: "stroke", cursor: "pointer" }}
                  onMouseEnter={() => setHoverEdge(e._uid)}
                  onMouseLeave={() => setHoverEdge(h => (h === e._uid ? null : h))}
                  onClick={() => setRightPanel({ edgeUid: e._uid })}
                />
                <path
                  d={d} fill="none"
                  strokeDasharray={e.condition ? "6 5" : undefined}
                  {...strokeFor(hot)}
                  {...(e.condition ? { stroke: selectorAccent(e.condition.selector_id) } : {})}
                />
              </g>
            );
          })}
        </svg>

      </>
    );
  };

  const renderCanvas = () => (
    <div
      ref={canvasRef}
      style={{
        flex: "1 1 0", minWidth: 0, overflow: "auto",
        background: CANVAS_BG, borderRadius: "16px",
        border: "1px solid rgba(199,210,254,0.7)",
        minHeight: "560px", maxHeight: "78vh", position: "relative",
      }}
    >
      {/* toolbar */}
      <div style={{
        position: "sticky", top: 0, left: 0, zIndex: 8,
        display: "flex", alignItems: "center", gap: "8px",
        padding: "10px 14px", background: "rgba(255,255,255,0.9)",
        borderBottom: `1px solid ${LINE}`, backdropFilter: "blur(4px)",
      }}>
        <button
          onClick={() => openAdd(null)}
          style={{ padding: "7px 14px", fontSize: "12px", fontWeight: 700, background: ACCENT, color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer" }}
        >
          + Add Agent (parallel branch)
        </button>
        {connect ? (
          <>
            <span style={{ fontSize: "11px", fontWeight: 600, color: ACCENT }}>
              {connect.mode === "from" ? "Edge from" : "Edge into"} <strong>{labelOf(connect.uid)}</strong> — click a highlighted node.
            </span>
            <button style={{ ...outBtn, padding: "5px 10px", fontSize: "11px" }} onClick={() => setConnect(null)}>Cancel (Esc)</button>
          </>
        ) : (
          <span style={{ fontSize: "11px", color: MUTED }}>
            Click a node’s <strong>+</strong> port to add a node or an edge · click an edge to gate it behind a selector
          </span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
          {[["−", () => setZoom(z => clampZoom(z - 0.1))], ["+", () => setZoom(z => clampZoom(z + 0.1))]].map(([label, fn]) => (
            <button key={label} onClick={fn}
              style={{ width: "24px", height: "24px", borderRadius: "6px", border: `1px solid ${LINE}`, background: "#fff", color: "#334155", fontSize: "13px", fontWeight: 700, cursor: "pointer", lineHeight: 0 }}
            >{label}</button>
          ))}
          <button onClick={() => setZoom(1)}
            style={{ minWidth: "44px", height: "24px", borderRadius: "6px", border: `1px solid ${LINE}`, background: "#fff", color: MUTED, fontSize: "10px", fontWeight: 700, cursor: "pointer" }}
          >{Math.round(zoom * 100)}%</button>
          <button onClick={fitToWidth}
            style={{ height: "24px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${LINE}`, background: "#fff", color: MUTED, fontSize: "10px", fontWeight: 700, cursor: "pointer" }}
          >Fit</button>
        </div>
      </div>

      {showIssues && issues.list.length > 0 && agentNodes.length > 0 && (
        <div style={{
          position: "sticky", left: 0, zIndex: 7, margin: "10px 14px 0",
          border: "1px solid #fecaca", background: "#fef2f2", color: "#b91c1c",
          borderRadius: "12px", padding: "6px 12px", fontSize: "12px",
        }}>
          {issues.list.length} issue{issues.list.length !== 1 ? "s" : ""} to fix before saving — see the panel on the right.
        </div>
      )}

      {agentNodes.length === 0 ? (
        <div style={{ padding: "60px 20px", textAlign: "center" }}>
          <p style={{ fontSize: "13px", color: MUTED, margin: "0 0 14px" }}>
            Your workflow is empty. A template needs at least one agent node before it can be saved.
          </p>
          <button
            onClick={() => openAdd(null)}
            style={{ padding: "12px 28px", border: `2px dashed ${ACCENT}`, borderRadius: "12px", background: "transparent", color: ACCENT, fontWeight: 700, fontSize: "14px", cursor: "pointer" }}
          >
            Choose an Agent  +
          </button>
        </div>
      ) : (
        <div style={{ width: drawBox.width * zoom, height: drawBox.height * zoom, margin: "0 auto", position: "relative" }}>
          <div style={{
            position: "absolute", top: 0, left: 0,
            // the drawing box, not just the nodes: zooming out has to reveal the
            // detoured edges rather than scroll past where they were cut off
            width: drawBox.width, height: drawBox.height,
            transform: `scale(${zoom})`, transformOrigin: "top left",
          }}>
            <Terminal x={layout.start.x} y={layout.start.y} label="START" tone="start" />
            <Terminal x={layout.end.x} y={layout.end.y} label="END" tone="end" />

            {renderEdges()}

            {agentNodes.filter(n => layout.pos[n._uid]).map(n => (
              <AgentNode
                key={n._uid}
                node={n}
                pos={layout.pos[n._uid]}
                areaName={areaNameOf(n.group_id)}
                accent={areaAccent(n.group_id)}
                selected={selectedNode?._uid === n._uid}
                hovered={hoverNode === n._uid}
                connectState={connectStateOf(n._uid)}
                issueCount={(issues.byNode[n._uid] || []).length}
                menuDir={portMenu?.anchor === n._uid ? portMenu.dir : null}
                onSelect={() => (connect ? completeConnect(n._uid) : setRightPanel({ nodeUid: n._uid }))}
                onHover={setHoverNode}
                onPort={(dir) => setPortMenu(m =>
                  (m?.anchor === n._uid && m.dir === dir ? null : { anchor: n._uid, dir })
                )}
                onRemove={() => removeNode(n._uid)}
              />
            ))}

            {/* synthesizer */}
            {layout.pos[SYNTH_UID] && (
              <div
                onClick={() => (connect ? completeConnect(SYNTH_UID) : setRightPanel("synth"))}
                onMouseEnter={() => setHoverNode(SYNTH_UID)}
                onMouseLeave={() => setHoverNode(h => (h === SYNTH_UID ? null : h))}
                style={{
                  position: "absolute", left: layout.pos[SYNTH_UID].x, top: layout.pos[SYNTH_UID].y,
                  width: NODE_W, height: NODE_H, boxSizing: "border-box", display: "flex",
                  background: "#fff", borderRadius: "12px",
                  // same precedence as AgentNode: a valid target always shows green
                  border: `1px solid ${
                    connectStateOf(SYNTH_UID) === "valid" ? "#34d399"
                      : rightPanel === "synth" ? SYNTH_ACCENT
                        : LINE
                  }`,
                  boxShadow: connectStateOf(SYNTH_UID) === "valid"
                    ? `${CARD_SHADOW}, 0 0 0 2px #6ee7b7`
                    : rightPanel === "synth" ? `${CARD_SHADOW}, 0 0 0 2px #ddd6fe` : CARD_SHADOW,
                  opacity: connectStateOf(SYNTH_UID) === "invalid" ? 0.5 : 1,
                  cursor: connectStateOf(SYNTH_UID) === "invalid" ? "not-allowed" : "pointer",
                  zIndex: 2,
                }}
              >
                <span style={{ width: "3px", flexShrink: 0, borderRadius: "12px 0 0 12px", background: SYNTH_ACCENT }} />
                <div style={{ flex: 1, minWidth: 0, padding: "9px 10px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <div style={{ fontSize: "11px", fontWeight: 600, color: INK, ...truncate }}>
                    ✦ {synthName || "Synthesizer"}
                  </div>
                  <div style={{ fontSize: "10px", color: MUTED, marginTop: "2px" }}>Final synthesizer</div>
                </div>
              </div>
            )}

            {hoverCard && <HoverCard {...hoverCard} />}
            {edgeCard && <EdgeHoverCard {...edgeCard} />}

            {/* port menu — the click-catcher below it closes on any outside click */}
            {portMenu && layout.pos[portMenu.anchor] && (
              <>
                <div onClick={() => setPortMenu(null)} style={{ position: "absolute", inset: 0, zIndex: 8 }} />
                <PortMenu
                  pos={layout.pos[portMenu.anchor]}
                  dir={portMenu.dir}
                  canvasW={layout.width}
                  onAddNode={() => openAdd({ anchor: portMenu.anchor, dir: portMenu.dir })}
                  onAddEdge={() => startConnect(portMenu.anchor, portMenu.dir)}
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // ── list tab ──────────────────────────────────────────────────────────────

  const renderList = () => (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginBottom: "16px" }}>
        <button
          style={{ ...outBtn, padding: "9px 18px", fontSize: "13px", fontWeight: 700 }}
          onClick={() => { setPlannerSeen(true); setTab("planner"); }}
        >
          ✦ Plan with AI
        </button>
        <button
          style={{ padding: "9px 18px", fontSize: "13px", fontWeight: 700, background: P, color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer" }}
          onClick={() => { resetBuilder(); setTab("builder"); }}
        >
          + New Template
        </button>
      </div>

      {listError && (
        <p style={{ color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", padding: "10px 14px", marginBottom: "14px" }}>
          {listError}
        </p>
      )}

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr>
              {["Template ID", "Name", "Research Areas", "Agents", "Edges", "Actions"].map((h, i) => (
                <th key={h} style={{ background: P, color: "#fff", padding: "10px 12px", textAlign: i >= 3 && i <= 4 ? "center" : "left", fontWeight: 600, fontSize: "13px" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loadingList ? (
              [...Array(4)].map((_, i) => (
                <tr key={i}>
                  {[...Array(6)].map((__, j) => (
                    <td key={j} style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}` }}>
                      <div style={{ height: "14px", background: "#e5e7eb", borderRadius: "4px" }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : templates.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: "32px", textAlign: "center", color: "#9ca3af", borderBottom: `1px solid ${BORDER}` }}>
                  {pageIndex > 0
                    ? <>No templates on this page — use <strong>‹ Prev</strong> to go back.</>
                    : <>No templates yet. Click <strong>+ New Template</strong> to create one.</>}
                </td>
              </tr>
            ) : templates.map(tpl => {
              const plan = tpl.plan || {};
              const agentCount = (plan.nodes || []).filter(n => n.node_type === "agent").length;
              const areas = (plan.research_areas || []).map(r => r.name).join(", ");
              const condEdges = (plan.edges || []).filter(e => e.condition);
              const condCount = condEdges.length;
              // distinct selectors = how many decisions the run makes
              const selCount = new Set(condEdges.map(e => e.condition.selector_id).filter(Boolean)).size;
              return (
                <tr key={tpl.template_id}>
                  <td style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}`, fontFamily: "monospace", fontSize: "11px", color: "#6b7280" }}>
                    {tpl.template_id?.slice(0, 8)}…
                  </td>
                  <td style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}`, fontWeight: 600 }}>
                    {plan.template?.name || "—"}
                  </td>
                  <td style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}`, color: "#4b5563", maxWidth: "180px" }}>
                    {areas || "—"}
                  </td>
                  <td style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}`, textAlign: "center" }}>
                    <span style={{ ...chip, fontSize: "11px", padding: "2px 8px", background: "#e0e7ff", color: P }}>{agentCount}</span>
                  </td>
                  <td style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}`, textAlign: "center" }}>
                    <span style={{ ...chip, fontSize: "11px", padding: "2px 8px", background: "#f3f4f6", color: "#374151" }}>
                      {(plan.edges || []).length}
                    </span>
                    {condCount > 0 && (
                      <span
                        title={`${condCount} conditional edge${condCount !== 1 ? "s" : ""} across ${selCount || condCount} selector${(selCount || condCount) !== 1 ? "s" : ""}`}
                        style={{ ...chip, marginLeft: "4px", background: "#fef3c7", color: "#b45309" }}
                      >
                        {selCount || condCount} sel · {condCount} cond
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "10px 12px", borderBottom: `1px solid ${BORDER}`, whiteSpace: "nowrap" }}>
                    <button
                      style={{ padding: "5px 10px", fontSize: "11px", fontWeight: 600, background: PL, color: P, border: "1px solid #c7d2fe", borderRadius: "4px", cursor: "pointer", marginRight: "6px" }}
                      onClick={() => startEdit(tpl)}
                    >Edit</button>
                    <button
                      style={{ padding: "5px 10px", fontSize: "11px", fontWeight: 600, background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca", borderRadius: "4px", cursor: "pointer" }}
                      onClick={() => setDeleteTarget(tpl)}
                    >Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/*
        Cursor paging. Prev walks the trail of cursors already visited; Next is
        live only while the API is still handing one back, so the last page
        disables it. Both are hidden entirely on a single-page list.
      */}
      {(pageIndex > 0 || nextCursor) && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: "12px",
          padding: "12px 4px 2px",
        }}>
          <button
            style={{ ...outBtn, padding: "7px 14px", opacity: canGoPrev(pageIndex, loadingList) ? 1 : 0.45, cursor: canGoPrev(pageIndex, loadingList) ? "pointer" : "not-allowed" }}
            onClick={goPrevPage}
            disabled={!canGoPrev(pageIndex, loadingList)}
          >‹ Prev</button>
          <span style={{ fontSize: "12px", color: MUTED, minWidth: "62px", textAlign: "center" }}>
            Page {pageIndex + 1}
          </span>
          <button
            style={{ ...outBtn, padding: "7px 14px", opacity: canGoNext(nextCursor, loadingList) ? 1 : 0.45, cursor: canGoNext(nextCursor, loadingList) ? "pointer" : "not-allowed" }}
            onClick={goNextPage}
            disabled={!canGoNext(nextCursor, loadingList)}
          >Next ›</button>
        </div>
      )}
    </div>
  );

  // ── builder tab ───────────────────────────────────────────────────────────

  const renderBuilder = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* header bar with editable title + description — both required by the API */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", padding: "14px 18px", background: "#fff", border: `1px solid ${LINE}`, borderRadius: "12px" }}>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
          <input
            style={{ width: "100%", fontSize: "17px", fontWeight: 700, border: "none", outline: "none", color: INK, background: "transparent", minWidth: 0 }}
            placeholder="Untitled Workflow Template"
            value={templateName}
            onChange={e => setTemplateName(e.target.value)}
          />
          <textarea
            style={{
              width: "100%", fontSize: "12px", lineHeight: 1.5, color: MUTED, background: "transparent",
              border: "none", borderBottom: `1px solid ${templateDesc.trim() ? "transparent" : "#fcd34d"}`,
              outline: "none", resize: "vertical", minHeight: "34px", padding: "2px 0", fontFamily: "inherit",
            }}
            rows={2}
            placeholder="Describe what this workflow produces… (required)"
            value={templateDesc}
            onChange={e => setTemplateDesc(e.target.value)}
          />
        </div>
        <span style={{ fontSize: "11px", color: MUTED, flexShrink: 0, paddingTop: "6px" }}>
          {agentNodes.length} agent{agentNodes.length !== 1 ? "s" : ""} · {edges.length} edge{edges.length !== 1 ? "s" : ""}
        </span>
        <button style={{ ...outBtn, flexShrink: 0 }} onClick={() => { resetBuilder(); setTab("list"); }}>Cancel</button>
        <button
          style={{ padding: "8px 20px", fontSize: "13px", fontWeight: 700, background: saving ? "#9ca3af" : P, color: "#fff", border: "none", borderRadius: "6px", cursor: saving ? "not-allowed" : "pointer", flexShrink: 0 }}
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? "Saving…" : formMode === "edit" ? "Save Changes" : "Create"}
        </button>
      </div>

      {/* two-column: canvas + inspector */}
      <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
        {renderCanvas()}
        <div
          ref={panelRef}
          style={{ width: "min(400px, 34vw)", flexShrink: 0, background: "#fff", border: `1px solid ${LINE}`, borderRadius: "16px", minHeight: "560px", maxHeight: "78vh", overflow: "hidden", display: "flex", flexDirection: "column", position: "relative" }}
        >
          {renderRightPanel()}
        </div>
      </div>

      {/* only while the library list is actually on screen — gating here beats
          clearing the hover at every place the panel changes */}
      {libraryPeek && rightPanel === "add" && agentDescriptionFlyout()}
    </div>
  );

  // ── root render ───────────────────────────────────────────────────────────

  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {onClose && (
        <button type="button" onClick={onClose} style={{ ...outBtn, alignSelf: "flex-start", marginBottom: "16px" }}>Close</button>
      )}
      <h2 style={{ color: P, fontSize: "22px", fontWeight: 700, marginBottom: "22px" }}>Workflow Templates</h2>

      {/* tab bar */}
      <div style={{ display: "flex", gap: "2px", borderBottom: `2px solid ${BORDER}`, marginBottom: "28px" }}>
        {[
          { id: "list", label: "Templates" },
          { id: "planner", label: "✦ AI Planner" },
          { id: "builder", label: formMode === "edit" ? "Edit Template" : "New Template" },
        ].map(t => (
          <button key={t.id}
            style={{ padding: "10px 22px", fontSize: "14px", fontWeight: tab === t.id ? 700 : 500, color: tab === t.id ? P : "#6b7280", background: tab === t.id ? PL : "transparent", border: "none", borderBottom: `3px solid ${tab === t.id ? P : "transparent"}`, cursor: "pointer", borderRadius: "4px 4px 0 0" }}
            onClick={() => {
              if (t.id === "list") resetBuilder();
              if (t.id === "planner") setPlannerSeen(true);
              setTab(t.id);
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "list" && renderList()}
      {tab === "builder" && renderBuilder()}
      {/* kept mounted so the planner conversation survives a trip to the builder */}
      {plannerSeen && (
        <div style={{ display: tab === "planner" ? "block" : "none" }}>
          <WorkflowPlanner onUsePlan={usePlannerPlan} />
        </div>
      )}

      {/* delete confirmation modal */}
      {deleteTarget && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(17,24,39,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div style={{ background: "#fff", borderRadius: "16px", padding: "24px", maxWidth: "420px", width: "90%", boxShadow: "0 8px 30px rgba(0,0,0,0.15)" }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 10px", fontSize: "17px", fontWeight: 700, color: INK }}>Delete template?</h3>
            <p style={{ margin: "0 0 20px", fontSize: "14px", color: "#4b5563", lineHeight: 1.5 }}>
              <strong>{deleteTarget.plan?.template?.name}</strong> will be permanently deleted. This cannot be undone.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button style={{ padding: "8px 16px", borderRadius: "6px", border: `1px solid ${BORDER}`, background: "#fff", cursor: "pointer", fontWeight: 600, fontSize: "13px" }} onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
              <button style={{ padding: "8px 16px", borderRadius: "6px", border: "none", background: "#b91c1c", color: "#fff", cursor: deleting ? "not-allowed" : "pointer", fontWeight: 700, fontSize: "13px", opacity: deleting ? 0.7 : 1 }} onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowTemplates;
